import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Clock, AlertTriangle, Zap, Timer, Info, CheckCircle2 } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
interface GPTModelTimeoutNotificationProps {
  selectedModel: string;
  onShow?: () => void;
  onGenerationStart?: () => void;
}
const isGPTImageModel = (modelKey: string): boolean => {
  if (!modelKey) return false;
  const lowerModel = modelKey.toLowerCase();
  // Check for GPT Image models including provider-based ones
  return lowerModel.includes('gpt-image') || lowerModel.includes('gpt_image') || lowerModel.includes('gpt-image-1') || lowerModel.includes('provider-3/gpt-image') || lowerModel.includes('provider-3/gpt-image-1');
};
const GPTModelTimeoutNotification: React.FC<GPTModelTimeoutNotificationProps> = ({
  selectedModel,
  onShow,
  onGenerationStart
}) => {
  const [showCountdown, setShowCountdown] = useState(false);
  const [countdown, setCountdown] = useState(180); // 3 minutes in seconds

  // Only show toast when generation starts, not when model is selected
  const showGenerationToast = () => {
    if (isGPTImageModel(selectedModel)) {
      console.log('✅ GPT Image model generation started, showing notification');

      // Show enhanced toast notification
      toast(<div className="flex items-start space-x-3 p-2">
          <div className="flex-shrink-0">
            <div className="w-10 h-10 bg-gradient-to-br from-amber-400 to-orange-500 rounded-full flex items-center justify-center animate-pulse">
              <Timer className="w-5 h-5 text-white" />
            </div>
          </div>
          <div className="flex-1">
            <h4 className="font-semibold text-amber-900 mb-1">⏱️ GPT Image Generation Started</h4>
            <p className="text-sm text-amber-800 leading-relaxed">
              This advanced AI model generates higher quality images but takes <strong>2-3 minutes</strong> compared to other models (~30 seconds).
            </p>
            <div className="flex items-center space-x-2 mt-2">
              <Badge className="bg-amber-100 text-amber-800 text-xs">Premium Quality</Badge>
              <Badge className="bg-blue-100 text-blue-800 text-xs">Extended Processing</Badge>
            </div>
          </div>
        </div>, {
        duration: 8000,
        className: "bg-gradient-to-br from-amber-50 via-yellow-50 to-orange-50 border-2 border-amber-200 shadow-xl max-w-md"
      });

      // Trigger callback if provided
      if (onShow) {
        onShow();
      }
    }
  };

  // Start countdown when generation begins (this would be triggered by parent component)
  const startCountdown = () => {
    setShowCountdown(true);
    setCountdown(180);
    const timer = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          setShowCountdown(false);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };
  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };
  const progressPercentage = (180 - countdown) / 180 * 100;

  // Enhanced persistent alert when GPT model is selected
  if (isGPTImageModel(selectedModel)) {
    return <div className="space-y-3">
        {/* Main Alert */}
        

        {/* Countdown Timer (shown during generation) */}
        {showCountdown && <Alert className="border border-blue-400/40 bg-gradient-to-r from-blue-500/10 to-purple-500/10 backdrop-blur-sm">
            <Timer className="h-4 w-4 text-blue-400 animate-spin" />
            <AlertDescription>
              <div className="flex items-center justify-between">
                <span className="text-blue-200 text-sm font-medium">
                  Generating with GPT Image Model... {formatTime(countdown)} remaining
                </span>
                <Badge className="bg-blue-100 text-blue-800 text-xs">
                  {Math.round(progressPercentage)}% Complete
                </Badge>
              </div>
              <Progress value={progressPercentage} className="mt-2 h-2 bg-blue-900/30" />
            </AlertDescription>
          </Alert>}
      </div>;
  }
  return null;
};

// Export the function to show toast when generation starts
export { GPTModelTimeoutNotification as default };
export const showGPTGenerationToast = (selectedModel: string) => {
  if (isGPTImageModel(selectedModel)) {
    toast(<div className="flex items-start space-x-3 p-2">
        <div className="flex-shrink-0">
          <div className="w-10 h-10 bg-gradient-to-br from-amber-400 to-orange-500 rounded-full flex items-center justify-center animate-pulse">
            <Timer className="w-5 h-5 text-white" />
          </div>
        </div>
        <div className="flex-1">
          <h4 className="font-semibold text-amber-900 mb-1">⏱️ GPT Image Generation Started</h4>
          <p className="text-sm text-amber-800 leading-relaxed">
            This advanced AI model generates higher quality images but takes <strong>2-3 minutes</strong> compared to other models (~30 seconds).
          </p>
          <div className="flex items-center space-x-2 mt-2">
            <Badge className="bg-amber-100 text-amber-800 text-xs">Premium Quality</Badge>
            <Badge className="bg-blue-100 text-blue-800 text-xs">Extended Processing</Badge>
          </div>
        </div>
      </div>, {
      duration: 8000,
      className: "bg-gradient-to-br from-amber-50 via-yellow-50 to-orange-50 border-2 border-amber-200 shadow-xl max-w-md"
    });
  }
};